// ─────────────────────────────────────────────────────────────────────────────
// CPE-138 — "Campañas por Localidad" (excepciones). Sub-vista INLINE dentro de la pestaña
// Fundamentales: tabla de excepciones + bloque de Aprobaciones + asistente de 3 pasos
// (Alcance → Documentos → Visibilidad). Un Admin OU las PROPONE (nacen pendientes + inactivas);
// el Admin Global las aprueba/rechaza. Cableado 1:1 al CampaignExceptionsController.
// ─────────────────────────────────────────────────────────────────────────────
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCampaignExceptions,
  useSaveCampaignException,
  useDeleteCampaignException,
  useToggleCampaignException,
  useResolveCampaignExceptionApproval,
  useCampaignExceptionDocuments,
  useAddCampaignExceptionDocument,
  useDeleteCampaignExceptionDocument,
  useUserById,
  useCampaigns,
  getCampaignName,
  getRecognitionErrorMessage,
} from '@/api/recognition';
import type {
  CampaignExceptionDto,
  CampaignExceptionType,
  CampaignExceptionOverrideDto,
  CampaignVisibilityDto,
  CreateCampaignExceptionBody,
} from '@/api/recognition';
import type { OUItem } from '@/api/types';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { OuTreeSelect } from '@/components/common/OuTree';
import { ScopePopover, ConfirmModal } from '@/components/common';
import { useOUItems } from '@/hooks/useOUs';
import { StateBlock } from '../components';
import { AdminSaveBar, AdminPagerFooter, AdminToggle, ToggleHelp, usePagination, BannerEditorFields, DocumentsManager } from './adminShared';
import { campanaFueraDeAlcance, expandOuIds, idsEnAlcance } from './ouScopeUtils';

/** Firma mínima de la función de traducción tal como la usa este archivo (clave + interpolaciones). */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Frecuencias de topes: clave interna ↔ valor que espera el backend (español).
type FreqKey = 'weekly' | 'daily' | 'monthly';
const FREQ_TO_BACKEND: Record<FreqKey, string> = { weekly: 'Semanal', daily: 'Diario', monthly: 'Mensual' };

// RN-PI03 — qué puertas de envío están abiertas para el alcance de la excepción. Mismos 3 valores
// que espera el backend en `CampaignExceptionOverrideDto.EntryPointMode`.
type EntryPointModeKey = 'app' | 'postit' | 'ambos';
const ENTRY_POINT_MODE_LABEL_KEYS: Record<EntryPointModeKey, string> = {
  app: 'admin.campaigns.exc.entryPointModeApp',
  postit: 'admin.campaigns.exc.entryPointModePostit',
  ambos: 'admin.campaigns.exc.entryPointModeAmbos',
};

// `expandOuIds` vive en `./ouScopeUtils` (SDD 2026-08-17 Tarea 5) — antes duplicada aquí, ahora
// compartida con `AdminCampaigns.tsx`.

interface ScopeInfo {
  rootNames: string[];
  siteNames: string[];
  fullOu: boolean;
}

/**
 * Deriva el breadcrumb del alcance (raíces + nodos más profundos) SIN hardcodear: las raíces son los
 * ids sin padre dentro de la selección; el resto son los "sites"; `fullOu` señala una única raíz cuyo
 * subárbol completo está incluido (badge "Toda la OU").
 */
export function describeScope(alcance: string[], items: OUItem[]): ScopeInfo {
  const byId = new Map(items.map((item) => [item.id, item]));
  const set = new Set(alcance);
  const roots: string[] = [];
  const deeper: string[] = [];
  for (const id of alcance) {
    const parent = byId.get(id)?.parentOuId ?? null;
    if (!parent || !set.has(parent)) roots.push(id);
    else deeper.push(id);
  }
  const nameOf = (id: string) => byId.get(id)?.name ?? id;
  let fullOu = false;
  if (roots.length === 1) {
    const full = expandOuIds(roots, items);
    fullOu = full.length > 1 && full.every((id) => set.has(id));
  }
  return { rootNames: roots.map(nameOf), siteNames: deeper.map(nameOf), fullOu };
}

/** Etiqueta legible del alcance para persistir en `alcanceEtiqueta`. */
function scopeLabel(alcance: string[], items: OUItem[]): string {
  const info = describeScope(alcance, items);
  const parts = [...info.rootNames];
  if (info.siteNames.length > 0) parts.push(`${info.siteNames.length} sites`);
  return parts.join(' › ');
}

// ─── Borrador del asistente ──────────────────────────────────────────────────
// Tarea 15 (SDD 2026-08-17) — el paso "Documentos" ya NO guarda su estado aquí (`ExcDoc[]` era un
// mockup que nunca se enviaba al backend, ver `buildBody`). Los documentos se suben/borran con
// llamadas SEPARADAS una vez que la excepción tiene `id`, mismo patrón que `CampaignDocumentsStep`
// (`DocumentsManager` en `adminShared.tsx`) — no viajan en `ExceptionDraft`.
interface ExceptionDraft {
  id?: string;
  /**
   * Bug UAT #114 — campaña de la que cuelga esta excepción. `null` = sin declarar, que es como se
   * guardaron TODAS hasta ahora («la maqueta no tiene selector de campaña»).
   *
   * Es INFORMATIVO: no acota a quién aplica la excepción —eso sigue siendo alcance + programa— sino
   * que hace visible la relación y permite mostrar la ventana de fechas de la campaña base. Ese era
   * el hueco de fondo del #114: «las de acá son hijas de las de arriba, pero no hay nada que las
   * ligue», y sin el vínculo el administrador no tenía forma de saber que su excepción necesita una
   * campaña activa debajo para hacer algo.
   *
   * Solo existe en el contexto Brillamos: Fundamentales no tiene campañas (es el default siempre
   * activo), así que ahí no hay nada que elegir.
   */
  baseCampaignId: string | null;
  alcance: string[];
  alcanceEtiqueta: string;
  disableSend: boolean; // kill-switch: excepción tipo DesactivarEnvio (toggle fa-ban de la maqueta)
  // Horarios (solo Brillamos): ventana fecha/hora + zona horaria. scheduleEnabled ⇒ tipo HorarioRestringido.
  scheduleEnabled: boolean;
  fechaInicio: string; // datetime-local "YYYY-MM-DDTHH:mm" (hora de pared en zonaHoraria)
  fechaFin: string;
  zonaHoraria: string; // IANA
  // RN-PI03 — override de qué puertas de envío están abiertas para el alcance de esta excepción.
  entryPointMode: EntryPointModeKey;
  /**
   * 🔴 Punto 3 del bug UAT #114 — «sobrescribir» explícito por bloque. Hasta ahora el asistente
   * mandaba TODO el paso Visibilidad siempre y con valores por defecto, así que el backend no podía
   * distinguir «no lo configuré» de «lo dejé igual al global» y tenía que gatearlo por `Tipo`: si la
   * excepción hacía cualquier otra cosa (tope, banner, horario), estas doce configuraciones se
   * ignoraban EN SILENCIO.
   *
   * Con el interruptor apagado el bloque viaja en `null` = «hereda del global», que es lo que el
   * backend ya sabe componer (`LocalityRules.Con` trata null como sin-override campo a campo). Mismo
   * patrón que Racha (`streakEnabled`) y Topes (`limitsEnabled`), que ya lo tenían.
   */
  visOverride: boolean;
  reglasOverride: boolean;
  modOverride: boolean;
  entryOverride: boolean;
  visText: boolean;
  visFundamental: boolean;
  visSender: boolean;
  visHabit: boolean;
  visRanking: boolean;
  // RN-E03 — visMultiFund alimenta `override.multiFundamental`; maxFundamentales solo aplica
  // cuando visMultiFund está activo (mismo patrón condicional que topeMaximo/topeFrecuencia).
  visMultiFund: boolean;
  maxFundamentales: number;
  ruleMultiPersona: boolean;
  ruleComment: boolean;
  ruleMinChars: number;
  ruleCsv: boolean;
  modReport: boolean;
  // El backend solo tiene UN flag `editarYEliminar` ("viajan juntos, como el global" — el propio
  // global de Gestión, `allowPostEdit`, tampoco distingue editar de eliminar). Un solo toggle, no
  // dos, para no capturar en la UI una combinación que el DTO no puede representar.
  modEditDelete: boolean;
  streakEnabled: boolean;
  streakMin: number;
  streakPeriod: 'weekly' | 'monthly';
  limitsEnabled: boolean;
  limitMax: number;
  limitFreq: FreqKey;
  // Banner "Brillamos Juntos" por localidad (Tarea 14) — override del banner GLOBAL (solo Admin
  // Global) para el alcance de esta excepción. Sin toggle propio: {} = "no configurado, hereda el
  // global" (mismo criterio que `buildOverride` para estos 2 campos).
  bannerSubtitle: Record<string, string>;
  bannerLogoUrl: Record<string, string>;
}

export function newDraft(): ExceptionDraft {
  return {
    baseCampaignId: null,
    alcance: [], alcanceEtiqueta: '',
    disableSend: false,
    scheduleEnabled: false, fechaInicio: '', fechaFin: '', zonaHoraria: EXC_DEFAULT_TZ,
    entryPointMode: 'ambos',
    // Apagados por defecto: una excepción nueva no debe pisar la configuración global de nada
    // que el administrador no haya tocado a propósito.
    visOverride: false, reglasOverride: false, modOverride: false, entryOverride: false,
    visText: true, visFundamental: true, visSender: true, visHabit: true, visRanking: true,
    visMultiFund: false, maxFundamentales: 2,
    ruleMultiPersona: true, ruleComment: true, ruleMinChars: 20, ruleCsv: true,
    modReport: true, modEditDelete: true,
    streakEnabled: false, streakMin: 1, streakPeriod: 'weekly',
    limitsEnabled: false, limitMax: 3, limitFreq: 'weekly',
    bannerSubtitle: {}, bannerLogoUrl: {},
  };
}

function backendToFreq(value: string | null | undefined): FreqKey {
  const key = (value ?? '').toLowerCase();
  if (key === 'diario') return 'daily';
  if (key === 'mensual') return 'monthly';
  return 'weekly';
}

/** Mapea una excepción existente al borrador editable del asistente. */
export function toDraft(exc: CampaignExceptionDto): ExceptionDraft {
  const base = newDraft();
  const o = exc.override ?? {};
  const v = o.visibilidad ?? null;
  return {
    ...base,
    id: exc.id,
    baseCampaignId: exc.baseCampaignId ?? null,
    alcance: [...exc.alcance],
    alcanceEtiqueta: exc.alcanceEtiqueta ?? '',
    disableSend: o.desactivarEnvio === true,
    scheduleEnabled: exc.tipo === 'HorarioRestringido' || o.fechaInicio != null || o.fechaFin != null,
    fechaInicio: isoToDatetimeLocal(o.fechaInicio, o.zonaHoraria || base.zonaHoraria),
    fechaFin: isoToDatetimeLocal(o.fechaFin, o.zonaHoraria || base.zonaHoraria),
    zonaHoraria: o.zonaHoraria || base.zonaHoraria,
    // Por PRESENCIA del dato guardado, no por el `Tipo`: así el ida y vuelta cierra. Derivarlo del
    // tipo haría que una excepción tipada TopeSemanal con reglas guardadas abriera con el interruptor
    // apagado y las borrara al volver a guardar.
    visOverride: v != null,
    reglasOverride: o.multipersona != null || o.comentarioObligatorio != null || o.cargaCsv != null,
    modOverride: o.permiteReportar != null || o.editarYEliminar != null,
    entryOverride: o.entryPointMode != null,
    visText: v ? v.mostrarTexto : base.visText,
    visFundamental: v ? v.mostrarFundamental : base.visFundamental,
    visSender: v ? v.mostrarRemitente : base.visSender,
    visHabit: v ? v.mostrarHabito : base.visHabit,
    // `o.cargaCsv` (Tarea 9) manda sobre el `v.cargaMasiva` legado cuando ambos existen; una
    // excepción creada ANTES de esta tarea solo trae `v.cargaMasiva`.
    ruleCsv: o.cargaCsv ?? (v ? v.cargaMasiva : base.ruleCsv),
    visMultiFund: o.multiFundamental ?? base.visMultiFund,
    maxFundamentales: o.maxFundamentales ?? base.maxFundamentales,
    ruleMultiPersona: o.multipersona ?? base.ruleMultiPersona,
    ruleComment: o.comentarioObligatorio ?? base.ruleComment,
    ruleMinChars: o.minCaracteres ?? base.ruleMinChars,
    modEditDelete: o.editarYEliminar ?? base.modEditDelete,
    entryPointMode: (o.entryPointMode as EntryPointModeKey | null | undefined) ?? base.entryPointMode,
    streakEnabled: o.rachaMinimoSemanal != null,
    streakMin: o.rachaMinimoSemanal ?? base.streakMin,
    limitsEnabled: o.topeMaximo != null,
    limitMax: o.topeMaximo ?? base.limitMax,
    limitFreq: o.topeFrecuencia ? backendToFreq(o.topeFrecuencia) : base.limitFreq,
    bannerSubtitle: o.bannerSubtitle ?? base.bannerSubtitle,
    bannerLogoUrl: o.bannerLogoUrl ?? base.bannerLogoUrl,
    visRanking: o.mostrarRanking ?? base.visRanking,
    modReport: o.permiteReportar ?? base.modReport,
  };
}

/** true si al menos un idioma trae subtítulo u logo de banner propio (Tarea 14). */
function hasBannerOverride(draft: ExceptionDraft): boolean {
  return Object.keys(draft.bannerSubtitle).length > 0 || Object.keys(draft.bannerLogoUrl).length > 0;
}

/**
 * El "tipo" lo dicta el override principal (no hay selector explícito — 1:1 con la maqueta, es una
 * etiqueta derivada). Precedencia: desactivar envío (kill-switch) → horario → tope → banner → en su
 * defecto Visibilidad. El backend gatea el enforcement por este Tipo (DesactivarEnvio/TopeSemanal/
 * HorarioRestringido en CreateRecognitionCommandHandler), por eso debe reflejar el efecto principal.
 * Banner va justo antes del default: cede precedencia ante cualquiera de los tres anteriores, así
 * que en un draft que ADEMÁS activa `disableSend`/`scheduleEnabled`/`limitsEnabled` el `tipo` sale
 * como el de esa regla, no `'Banner'`. `GetLandingBannerQueryHandler` (backend) filtra estrictamente
 * por `Tipo == Banner` — igual que el resto de tipos, solo uno puede "ganar" por excepción — así que
 * en ese escenario el banner queda guardado en `override.bannerSubtitle/bannerLogoUrl` pero INERTE
 * (nunca se aplica). Limitación preexistente del modelo "un solo Tipo por excepción" (ya la heredó
 * Visibilidad/RN-E03 en la Tarea 9); no es un bug introducido por el paso Banner de la Tarea 14.
 */
function deriveTipo(draft: ExceptionDraft): CampaignExceptionType {
  // 🔴 El tipo se deriva del INTERRUPTOR (`scheduleEnabled`), NO de que haya fechas capturadas, y es
  // deliberado. `buildOverride` sí persiste la ventana por valor —para no descartar lo tecleado—, pero
  // si además cambiara el tipo, este pasaría a `HorarioRestringido` y la familia VISIBILIDAD dejaría de
  // aplicarse: el backend la sigue gateando por `Tipo == Visibilidad`
  // (`LocalityExceptionEnforcement`), y esa familia es media pantalla del paso Visibilidad
  // (visibilidad, reglas de envío, moderación, racha). Capturar unas fechas apagaría todo eso en
  // silencio.
  //
  // ⚠️ La trampa sigue viva por el otro lado: ENCENDER el interruptor sí cambia el tipo y sí apaga esa
  // familia. Es preexistente, no la introduce este cambio — y es el argumento para levantar el gate de
  // Visibilidad (pendiente: exige que el asistente sepa mandar ese bloque en `null`).
  if (draft.disableSend) return 'DesactivarEnvio';
  if (draft.scheduleEnabled) return 'HorarioRestringido';
  if (draft.limitsEnabled) return 'TopeSemanal';
  if (hasBannerOverride(draft)) return 'Banner';
  return 'Visibilidad';
}

function buildOverride(draft: ExceptionDraft): CampaignExceptionOverrideDto {
  const visibilidad: CampaignVisibilityDto = {
    mostrarRemitente: draft.visSender,
    mostrarFundamental: draft.visFundamental,
    mostrarTexto: draft.visText,
    mostrarHabito: draft.visHabit,
    enviados: true,
    recibidos: true,
    anonimos: false,
    // RN-PI03 — `entryPointMode` (abajo) es el override REAL de puertas de envío para excepciones;
    // este campo legado de `CampaignVisibilityDto` no lo lee `LocalityVisibility` para excepciones,
    // así que se manda fijo en 'ambos' (mismo valor que ya salía siempre por default).
    entryMode: 'ambos',
    cargaMasiva: draft.ruleCsv,
    aplicanPuntos: false,
    reaccionesHabilitadas: true,
    // B8-3 — el panel de excepción no expone estos cuatro (son del asistente de CAMPAÑA);
    // se mandan en su default para no alterar la campaña base al guardar la excepción.
    mostrarEtiquetaCampana: true,
    permitirDesdeFundamentales: true,
    envioDesactivado: false,
  };
  /** Hay ventana que guardar: la pidió el interruptor, o el admin capturó las dos fechas. */
  const guardaVentana = draft.scheduleEnabled || (!!draft.fechaInicio && !!draft.fechaFin);
  return {
    desactivarEnvio: draft.disableSend ? true : null,
    // Horarios: la ventana se manda como ISO UTC (offset resuelto desde la zona horaria elegida) para
    // que el backend compare DateTimeOffset directo.
    //
    // 🔴 Se persiste si el interruptor está encendido O si hay una ventana capturada — antes exigía
    // SOLO `scheduleEnabled`, y eso descartaba en silencio lo que el administrador acababa de teclear
    // (bug UAT #114). El primer arreglo deshabilitó los campos para que no se pudiera teclear algo que
    // no se iba a guardar; se descartó al probarlo en QA: quitaba una capacidad que sí existía y
    // obligaba a encender un interruptor rotulado «Desactivar reconocimientos» para poder poner unas
    // fechas, que nadie va a hacer.
    //
    // Ahora es seguro derivarlo del valor, y esto es la parte importante: hasta hoy, hacerlo habría
    // cambiado el `Tipo` a HorarioRestringido y ese tipo GATEABA el enforcement, así que el tope o el
    // banner de la misma excepción habrían dejado de aplicarse. El punto 1 de este mismo día quitó ese
    // gate —cada override se aplica por presencia de su valor—, así que el `Tipo` ya es solo una
    // etiqueta y derivarlo no suprime nada.
    //
    // Simétrico con la LECTURA: `toDraft` ya deducía `scheduleEnabled` de que hubiera fechas. El
    // escritor exigía el interruptor y el lector no: esa asimetría era el defecto de fondo.
    fechaInicio: guardaVentana ? wallClockToUtcIso(draft.fechaInicio, draft.zonaHoraria) : null,
    fechaFin: guardaVentana ? wallClockToUtcIso(draft.fechaFin, draft.zonaHoraria) : null,
    zonaHoraria: guardaVentana ? draft.zonaHoraria : null,
    topeMaximo: draft.limitsEnabled ? draft.limitMax : null,
    topeFrecuencia: draft.limitsEnabled ? FREQ_TO_BACKEND[draft.limitFreq] : null,
    rachaMinimoSemanal: draft.streakEnabled ? draft.streakMin : null,
    // Punto 3 — cada bloque solo viaja si su interruptor lo pide. `null` = «hereda del global», que es
    // lo que `LocalityRules.Con` ya sabe componer campo a campo. Antes se mandaba TODO siempre y con
    // los valores por defecto del formulario, y por eso el backend tenía que gatearlo por `Tipo`.
    visibilidad: draft.visOverride ? visibilidad : null,
    // RN-E03/RN-PI03 (Tarea 9) — reglas de envío por localidad. multiFundamental/multipersona/
    // comentarioObligatorio/editarYEliminar/cargaCsv/entryPointMode se mandan SIEMPRE con el valor
    // elegido (igual que `visibilidad`, que tampoco se gatea): el asistente no ofrece un estado
    // "hereda", así que cada excepción declara su propia foto completa de estas reglas.
    // maxFundamentales/minCaracteres sí quedan null cuando su toggle gobernante está apagado, igual
    // que topeMaximo/topeFrecuencia.
    multiFundamental: draft.visOverride ? draft.visMultiFund : null,
    maxFundamentales: draft.visOverride && draft.visMultiFund ? draft.maxFundamentales : null,
    multipersona: draft.reglasOverride ? draft.ruleMultiPersona : null,
    comentarioObligatorio: draft.reglasOverride ? draft.ruleComment : null,
    minCaracteres: draft.reglasOverride && draft.ruleComment ? draft.ruleMinChars : null,
    editarYEliminar: draft.modOverride ? draft.modEditDelete : null,
    cargaCsv: draft.reglasOverride ? draft.ruleCsv : null,
    entryPointMode: draft.entryOverride ? draft.entryPointMode : null,
    // Tarea 14 — banner de Brillamos Juntos por localidad. Igual que el resto de campos "por idioma"
    // de este DTO: null cuando no hay ningún idioma configurado (hereda el banner global); el objeto
    // completo cuando sí lo hay — SIEMPRE que haya contenido, sin importar qué otro override tenga
    // activo el draft (mismo criterio "manda la foto completa" que el resto de campos "por idioma").
    // 🔴 Eso es independiente de si el banner llega a APLICARSE: el backend
    // (`GetLandingBannerQueryHandler`) solo lee el override de una excepción cuyo `Tipo == Banner`.
    // Si `deriveTipo` produjo otro tipo (p. ej. `disableSend` activo ⇒ `DesactivarEnvio`), estos 2
    // campos quedan guardados en Mongo pero inertes — no se ven en el landing. Mismo comportamiento
    // ya existente para `visibilidad`/RN-E03 desde la Tarea 9 (un solo `Tipo` por excepción), no algo
    // nuevo de este paso.
    bannerSubtitle: Object.keys(draft.bannerSubtitle).length > 0 ? draft.bannerSubtitle : null,
    bannerLogoUrl: Object.keys(draft.bannerLogoUrl).length > 0 ? draft.bannerLogoUrl : null,
    // "Mostrar Ranking"/"Permitir reportar" — igual que el resto de reglas de este paso, se mandan
    // SIEMPRE con el valor elegido (el asistente no ofrece un estado "hereda" para ninguno de los dos).
    mostrarRanking: draft.visOverride ? draft.visRanking : null,
    permiteReportar: draft.modOverride ? draft.modReport : null,
  };
}

export function buildBody(draft: ExceptionDraft, items: OUItem[], context: ExcContext = 'fundamentales'): CreateCampaignExceptionBody {
  const alcance = expandOuIds(draft.alcance, items);
  return {
    tipo: deriveTipo(draft),
    programa: context === 'brillamos' ? 'Brillamos' : 'Fundamentales',
    alcance,
    override: buildOverride(draft),
    // Bug UAT #114 — antes iba en duro a `null` («la maqueta no tiene selector de campaña»). Ahora viaja
    // lo que el administrador eligió, y `null` sigue significando «sin declarar», que es como están todas
    // las que ya existen. Solo el contexto Brillamos ofrece el selector: Fundamentales no tiene campañas.
    //
    // El backend dejó de filtrar por `BaseCampaignId == null` en sus TRES consultas de runtime junto con
    // este cambio. Si no, una excepción enlazada se guardaría, se aprobaría, se activaría… y no aplicaría
    // nada: desaparecería de la resolución sin decir una palabra.
    baseCampaignId: context === 'brillamos' ? draft.baseCampaignId : null,
    // Recalcular SIEMPRE desde el alcance actual: al editar, draft.alcanceEtiqueta trae el label viejo
    // y usarlo persistiría un snapshot desalineado si el alcance cambió.
    alcanceEtiqueta: scopeLabel(draft.alcance, items),
  };
}

const clampNumber = (raw: string, min: number, max: number, fallback: number): number => {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

// ─── Nombre del solicitante (degrada al id si el directorio no responde) ───────
function RequesterName({ userId }: Readonly<{ userId: string }>) {
  const { data } = useUserById(userId);
  return <>{data?.displayName?.trim() || userId}</>;
}

// ─── Banner de pendientes (solo quien puede aprobar) ───────────────────────────
//
// Bug UAT (23-ago-2026): el banner decía «Tienes 3 excepción(es) esperando tu aprobación» con la
// tabla de abajo mostrando dos filas YA resueltas (Aprobada / Rechazada). La causa es que el banner
// y la tabla no leían la misma fuente:
//
//   · banner → GET campaign-exceptions/approvals/pending. Su handler
//     (`GetPendingCampaignExceptionApprovalsQueryHandler` → `GetPendingAsync`) filtra por tenant +
//     `EstadoAprobacion == "pendiente"` + no borrada, y NADA MÁS: ni el alcance delegado del actor
//     (`AdminScopeOuIds`) ni el `programa`.
//   · tabla → GET campaign-exceptions (`useCampaignExceptions({ programa })`), que el backend SÍ
//     acota por las dos cosas (`GetExceptions` pasa `adminScopeOuIds`, y el `programa` va en la query).
//
// De ahí los dos síntomas del ticket: (1) el conteo ignoraba el alcance —un admin limitado a FOSE
// veía el número global, aunque la tabla ya le respetara el permiso— y (2) ignoraba el programa, así
// que un Admin General parado en la pestaña de Brillamos sumaba las pendientes de Fundamentales, que
// esa tabla no pinta.
//
// El conteo pasa a salir del MISMO listado que pinta la tabla, filtrado por `estado: 'pendiente'`.
// Se lee `totalCount` y no `items.length` a propósito: el listado viene PAGINADO (pageSize 20 por
// defecto en el backend), así que contar los items de la primera página mentiría en cuanto hubiera
// más de una; `pageSize: 1` pide el total sin arrastrar payload. El alcance y el programa los sigue
// aplicando el backend — no se duplica la regla aquí.
function PendingBanner({ context }: Readonly<{ context: ExcContext }>) {
  const { t } = useTranslation('recognition');
  const pending = useCampaignExceptions({
    programa: context === 'brillamos' ? 'Brillamos' : 'Fundamentales',
    estado: 'pendiente',
    pageSize: 1,
  });
  const count = pending.data?.totalCount ?? 0;
  if (count === 0) return null;
  return (
    <p className="adm-note adm-note--warn">
      <i className="fas fa-clock" aria-hidden="true" /> {t('admin.campaigns.exc.pendingBanner', { count })}
    </p>
  );
}

// ─── Panel principal (tabla + aprobaciones + asistente) ────────────────────────
type ExcMode = { kind: 'table' } | { kind: 'wizard'; draft: ExceptionDraft };

/**
 * `isReadOnly` — SOLO lo usa la pestaña Fundamentales de `AdminCampaigns.tsx`, cuando su selector
 * "Vista" está en Admin OU/Embajador (simulación de solo-lectura de la maqueta): ese modo pintaba
 * la config general en solo lectura pero dejaba este panel, montado como hermano, con el botón
 * "+ Nueva excepción por OU" igual de activo — inconsistente con "esta vista es de solo lectura".
 * NO gatea el alcance real de un Admin OU (que sí puede proponer excepciones): la pestaña de
 * Brillamos (`context="brillamos"`) no tiene ese selector y nunca pasa esta prop.
 */
export function CampaignExceptionsPanel({
  context = 'fundamentales', isReadOnly = false,
}: Readonly<{ context?: ExcContext; isReadOnly?: boolean }>) {
  const { t } = useTranslation('recognition');
  const [mode, setMode] = useState<ExcMode>({ kind: 'table' });
  // Tarea 6 (R2b) — árbol COMPLETO de 4 niveles (país/región → ... → departamento), no solo las 6
  // raíces: el admin necesita poder acotar una excepción a un departamento concreto, no solo a país.
  // activeOnly: false — ramas país inactivas (LATAM-ECUADOR, LATAM-PERU, ...) tienen hijos BU activos;
  // sin esto `buildOuTree` los pinta como raíces duplicadas sueltas en vez de anidados bajo su rama
  // real (mismo fix que `AdminRoles.tsx`/`OUsSubTab.tsx`).
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });

  const query = useCampaignExceptions({ programa: context === 'brillamos' ? 'Brillamos' : 'Fundamentales' });
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const pager = usePagination(items, 25);

  const deleteM = useDeleteCampaignException();
  const toggleM = useToggleCampaignException();
  const [confirmDelete, setConfirmDelete] = useState<CampaignExceptionDto | null>(null);
  // Confirmación antes de DETENER (desactivar) una excepción aprobada — activarla no la pide, solo
  // apagarla, que es el efecto disruptivo (deja de aplicar para su alcance).
  const [confirmDeactivate, setConfirmDeactivate] = useState<CampaignExceptionDto | null>(null);

  if (mode.kind === 'wizard') {
    return (
      <ExceptionWizard
        key={mode.draft.id ?? 'new'}
        draft={mode.draft}
        ouItems={ouItems}
        context={context}
        onDone={() => setMode({ kind: 'table' })}
      />
    );
  }

  return (
    <div className="exc-panel">
      <div className="exc-sep">
        <span className="exc-sep-title"><i className="fas fa-map-marker-alt exc-sep-icon" aria-hidden="true" /> {t('admin.campaigns.exc.title')}</span>
        {!isReadOnly && (
          <button type="button" className="btn btn--secondary" onClick={() => setMode({ kind: 'wizard', draft: newDraft() })}>
            <i className="fas fa-plus" /> {t('admin.campaigns.exc.new')}
          </button>
        )}
      </div>
      <p className="adm-note">{t('admin.campaigns.exc.intro')}</p>

      <StateBlock
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={items.length === 0}
        emptyText={t('admin.campaigns.exc.empty')}
        onRetry={() => { query.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.campaigns.exc.colException')}</th>
                <th>{t('admin.campaigns.exc.colScope')}</th>
                <th>{t('admin.campaigns.exc.colState')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pager.slice.map((exc) => {
                const scope = describeScope(exc.alcance, ouItems);
                const approved = exc.estadoAprobacion === 'aprobada';
                const stateClass = exc.activa ? 'adm-badge adm-badge--on exc-state-btn' : 'adm-badge exc-state-btn';
                return (
                  <tr key={exc.id}>
                    <td><strong>{summarize(exc, t)}</strong></td>
                    <td>
                      <span className="exc-scope-cell">
                        {scope.rootNames.map((name, i) => (
                          <Fragment key={name}>
                            {i > 0 && <span className="exc-scope-sep">›</span>}
                            <span className="exc-scope-crumb">{name}</span>
                          </Fragment>
                        ))}
                        {scope.fullOu && <span className="adm-badge adm-badge--info">{t('admin.campaigns.exc.fullOu')}</span>}
                        {scope.siteNames.length > 0 && (<>
                          <span className="exc-scope-sep">›</span>
                          <ScopePopover
                            triggerClassName="exc-sites-link"
                            triggerLabel={t('admin.campaigns.exc.sites', { count: scope.siteNames.length })}
                            title={t('admin.campaigns.exc.scopePopTitle')}
                            items={scope.siteNames}
                          />
                        </>)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={stateClass}
                        disabled={!approved || toggleM.isPending}
                        title={approved ? undefined : t('admin.campaigns.exc.toggleHint')}
                        onClick={() => (exc.activa ? setConfirmDeactivate(exc) : toggleM.mutate({ id: exc.id, activa: true }))}
                      >
                        {exc.activa ? t('admin.campaigns.exc.stateActive') : t('admin.campaigns.exc.stateInactive')}
                      </button>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button type="button" className="btn btn--link" onClick={() => setMode({ kind: 'wizard', draft: toDraft(exc) })}>
                          <i className="fas fa-pen" /> {t('admin.campaigns.exc.edit')}
                        </button>
                        <button type="button" className="btn btn--danger" disabled={deleteM.isPending} onClick={() => setConfirmDelete(exc)}>
                          <i className="fas fa-trash-can" /> {t('admin.campaigns.exc.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {items.length > 0 && (
          <AdminPagerFooter
            total={items.length}
            page={pager.page}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
            onPageSize={(s) => pager.setPageSize(s === 0 ? items.length : s)}
          />
        )}
      </StateBlock>
      {(deleteM.isError || toggleM.isError) && (
        <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(deleteM.error || toggleM.error, t('admin.saveError'))}</p>
      )}

      <ExceptionApprovals items={items} context={context} />

      {confirmDelete && (
        <ConfirmModal
          title={t('admin.campaigns.exc.deleteTitle')}
          message={t('admin.campaigns.exc.deleteMsg')}
          confirmText={t('admin.campaigns.exc.deleteConfirm')}
          onConfirm={() => deleteM.mutate(confirmDelete.id)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
      {confirmDeactivate && (
        <ConfirmModal
          type="warning"
          title={t('admin.campaigns.deactivateConfirmTitle')}
          message={t('admin.campaigns.deactivateConfirmMsg')}
          confirmText={t('admin.campaigns.panic')}
          onConfirm={() => toggleM.mutate({ id: confirmDeactivate.id, activa: false })}
          onClose={() => setConfirmDeactivate(null)}
        />
      )}
    </div>
  );
}

/** Resumen de la columna "Excepción" derivado del tipo + override. */
function summarize(exc: CampaignExceptionDto, t: TFn): string {
  const o = exc.override ?? {};
  if (exc.tipo === 'DesactivarEnvio') return t('admin.campaigns.exc.summaryDisable');
  if (exc.tipo === 'HorarioRestringido') return t('admin.campaigns.exc.summarySchedule');
  if (exc.tipo === 'TopeSemanal') {
    return t('admin.campaigns.exc.summaryTope', { max: o.topeMaximo ?? '—', freq: freqLabel(o.topeFrecuencia, t) });
  }
  if (exc.tipo === 'Banner') return t('admin.campaigns.exc.summaryBanner');
  return t('admin.campaigns.exc.summaryVisibility');
}

function freqLabel(value: string | null | undefined, t: TFn): string {
  const key = (value ?? '').toLowerCase();
  if (key === 'diario') return t('admin.campaigns.periodDaily');
  if (key === 'mensual') return t('admin.campaigns.periodMonthly');
  return t('admin.campaigns.periodWeekly');
}

// ─── Bloque de Aprobaciones ────────────────────────────────────────────────────
function ExceptionApprovals({ items, context }: Readonly<{ items: CampaignExceptionDto[]; context: ExcContext }>) {
  const { t, i18n } = useTranslation('recognition');
  const resolveM = useResolveCampaignExceptionApproval();
  const [rejecting, setRejecting] = useState<{ id: string; comment: string } | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<string | null>(null);
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
  const pager = usePagination(items, 25); // maqueta: la tabla de Aprobaciones lleva pie de paginado

  // Matriz de permisos de Reconocimiento — "Aprobar nominaciones" (approveNominations) era un
  // permiso muerto: este bloque estaba cableado solo a `AdminGlobal` por rol, así que activarlo
  // para un AdminBrillamos/AdminOUBrillamos no le daba ninguna capacidad. AdminGlobal se conserva
  // explícito (no pasa por la matriz de permisos granulares, igual que el resto del admin).
  const currentUser = useCurrentUser();
  const canApproveExceptions = (currentUser?.roles.includes('AdminGlobal') ?? false)
    || (currentUser?.recognitionPermissions?.includes('approveNominations') ?? false);

  const estadoPill = (estado: string): { cls: string; icon: string; label: string } => {
    if (estado === 'aprobada') return { cls: 'adm-badge adm-badge--on', icon: 'fa-check', label: t('admin.campaigns.exc.approved') };
    if (estado === 'rechazada') return { cls: 'adm-badge adm-badge--warn', icon: 'fa-xmark', label: t('admin.campaigns.exc.rejected') };
    return { cls: 'adm-badge adm-badge--warn', icon: 'fa-hourglass-half', label: t('admin.campaigns.exc.pending') };
  };

  return (
    <div className="exc-approvals">
      <div className="exc-sep">
        <span className="exc-sep-title"><i className="fas fa-clipboard-check exc-sep-icon exc-sep-icon--purple" aria-hidden="true" /> {t('admin.campaigns.exc.approvalsTitle')}</span>
      </div>
      {canApproveExceptions && <PendingBanner context={context} />}

      {items.length === 0 ? (
        <p className="adm-note">{t('admin.campaigns.exc.approvalsEmpty')}</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.campaigns.exc.colException')}</th>
                <th>{t('admin.campaigns.exc.approvalsColSolicitante')}</th>
                <th>{t('admin.campaigns.exc.approvalsColAprobador')}</th>
                <th>{t('admin.campaigns.exc.approvalsColFecha')}</th>
                <th>{t('admin.campaigns.exc.approvalsColEstado')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pager.slice.map((exc) => {
                const pill = estadoPill(exc.estadoAprobacion);
                const isPending = exc.estadoAprobacion === 'pendiente';
                return (
                  <Fragment key={exc.id}>
                    <tr>
                      <td>{summarize(exc, t)}</td>
                      <td><RequesterName userId={exc.solicitadoPor} /></td>
                      <td>{exc.resueltoPor ? <RequesterName userId={exc.resueltoPor} /> : '—'}</td>
                      <td>{fmt(exc.solicitadoAt)}</td>
                      <td><span className={pill.cls}><i className={`fas ${pill.icon}`} /> {pill.label}</span></td>
                      <td>
                        {isPending && canApproveExceptions && (
                          <div className="adm-actions">
                            <button type="button" className="btn btn--primary" disabled={resolveM.isPending} onClick={() => setConfirmApprove(exc.id)}>
                              <i className="fas fa-check" /> {t('admin.campaigns.exc.approve')}
                            </button>
                            <button type="button" className="btn btn--danger" disabled={resolveM.isPending} onClick={() => setRejecting({ id: exc.id, comment: '' })}>
                              <i className="fas fa-xmark" /> {t('admin.campaigns.exc.reject')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {rejecting?.id === exc.id && (
                      <tr>
                        <td colSpan={6}>
                          <div className="adm-reject-box">
                            <label className="adm-field">
                              <span>{t('admin.campaigns.exc.rejectReason')}</span>
                              <textarea rows={2} value={rejecting.comment} autoFocus onChange={(e) => setRejecting({ id: exc.id, comment: e.target.value })} />
                            </label>
                            <div className="adm-actions">
                              <button
                                type="button"
                                className="btn btn--danger"
                                disabled={!rejecting.comment.trim() || resolveM.isPending}
                                onClick={() => resolveM.mutate(
                                  { id: exc.id, action: 'reject', comentario: rejecting.comment.trim() },
                                  { onSuccess: () => setRejecting(null) },
                                )}
                              >
                                <i className="fas fa-xmark" /> {t('admin.campaigns.exc.rejectConfirm')}
                              </button>
                              <button type="button" className="btn btn--secondary" onClick={() => setRejecting(null)}>{t('common.cancel')}</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {items.length > 0 && (
        <AdminPagerFooter
          total={items.length}
          page={pager.page}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          onPageSize={(s) => pager.setPageSize(s === 0 ? items.length : s)}
        />
      )}
      {resolveM.isError && (
        <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(resolveM.error, t('admin.saveError'))}</p>
      )}
      {confirmApprove && (
        <ConfirmModal
          type="info"
          title={t('admin.campaigns.approveConfirmTitle')}
          message={t('admin.campaigns.approveConfirmMsg')}
          confirmText={t('admin.campaigns.exc.approve')}
          onConfirm={() => resolveM.mutate({ id: confirmApprove, action: 'approve' })}
          onClose={() => setConfirmApprove(null)}
        />
      )}
    </div>
  );
}

// ─── Asistente (3 pasos Fundamentales · 4 pasos Brillamos, antepone "Horarios") ────────────────────
// El contexto define el programa de la excepción y los pasos. La maqueta muestra el paso Horarios SOLO en
// el wizard de excepciones de Brillamos (sin selector de campaña → excepción a nivel programa).
type ExcContext = 'fundamentales' | 'brillamos';
type ExcStep = 'schedule' | 'banner' | 'scope' | 'docs' | 'visibility';
// El paso Banner, igual que Horarios, solo existe en el wizard de Brillamos: el banner "Brillamos
// Juntos" es un concepto propio de ese programa (Tarea 14), sin equivalente en Fundamentales.
const stepsFor = (context: ExcContext): ExcStep[] =>
  context === 'brillamos' ? ['schedule', 'banner', 'scope', 'docs', 'visibility'] : ['scope', 'docs', 'visibility'];
const EXC_STEP_ICONS: Record<ExcStep, string> = { schedule: 'fa-clock', banner: 'fa-image', scope: 'fa-globe', docs: 'fa-file-pdf', visibility: 'fa-eye' };
const EXC_STEP_LABELS: Record<ExcStep, string> = {
  schedule: 'admin.campaigns.exc.stepSchedule',
  banner: 'admin.campaigns.exc.stepBanner',
  scope: 'admin.campaigns.exc.stepScope',
  docs: 'admin.campaigns.exc.stepDocs',
  visibility: 'admin.campaigns.exc.stepVisibility',
};

// Zonas horarias (IANA) para el paso Horarios — mismo origen que el wizard de campaña.
const EXC_DEFAULT_TZ = 'America/Mexico_City';
const EXC_TIMEZONES: string[] = (() => {
  try { return Intl.supportedValuesOf?.('timeZone') ?? [EXC_DEFAULT_TZ]; } catch { return [EXC_DEFAULT_TZ]; }
})();

/**
 * Convierte una hora de PARED ("YYYY-MM-DDTHH:mm", del input datetime-local) interpretada en la zona
 * horaria IANA elegida, a un ISO UTC offset-aware — así el backend compara `DateTimeOffset` sin necesitar
 * TimeZoneInfo (no hay una sola línea de conversión TZ en ese repo). Devuelve null si la entrada es vacía.
 * NOTA: usa el offset en el instante "naive-como-UTC" (técnica estándar); en la ventana de ~1h de una
 * transición DST puede caer al lado adyacente por el delta DST. La zona default America/Mexico_City ya no
 * tiene DST (abolido 2022) → no afectada; otras zonas IANA sí, precisión aceptable para una ventana de campaña.
 */
export function wallClockToUtcIso(wallClock: string, timeZone: string): string | null {
  if (!wallClock) return null;
  const naiveAsUtc = new Date(`${wallClock}:00Z`);
  if (Number.isNaN(naiveAsUtc.getTime())) return null;
  // Offset de la zona en ese instante (min, positivo = adelantada a UTC), vía Intl.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(naiveAsUtc);
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;
  const asUtcMs = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  const offsetMin = Math.round((asUtcMs - naiveAsUtc.getTime()) / 60000);
  return new Date(naiveAsUtc.getTime() - offsetMin * 60000).toISOString();
}

/**
 * ISO instant → valor para `<input type="datetime-local">` mostrando la hora de PARED en `timeZone` (la
 * zona de la excepción), NO en UTC — si no, al editar se vería la hora UTC y guardar la correría por el
 * offset en cada ciclo. Inverso exacto de wallClockToUtcIso.
 */
function isoToDatetimeLocal(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// ─── Paso Documentos (Tarea 15 SDD 2026-08-17) ─────────────────────────────────
// El formulario de alta + tabla vive en `DocumentsManager` (`adminShared.tsx`), compartido con
// `CampaignDocumentsStep` (AdminCampaigns.tsx). Aquí solo se cablean los hooks reales de EXCEPCIÓN.
function ExceptionDocumentsStep({ exceptionId }: Readonly<{ exceptionId: string }>) {
  const docs = useCampaignExceptionDocuments(exceptionId);
  const addM = useAddCampaignExceptionDocument();
  const delM = useDeleteCampaignExceptionDocument();

  return (
    <DocumentsManager
      docs={docs.data ?? []}
      isLoading={docs.isLoading}
      isError={docs.isError}
      error={docs.error}
      onRetry={() => { docs.refetch(); }}
      onAdd={(input) => addM.mutate({ id: exceptionId, input })}
      addPending={addM.isPending}
      addError={addM.isError ? addM.error : null}
      addSuccess={addM.isSuccess}
      onAddReset={() => { if (addM.isSuccess || addM.isError) addM.reset(); }}
      onDelete={(documentId) => delM.mutate(documentId)}
      deletePending={delM.isPending}
    />
  );
}

export function ExceptionWizard({ draft: initial, ouItems, context, onDone }: Readonly<{ draft: ExceptionDraft; ouItems: OUItem[]; context: ExcContext; onDone: () => void }>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const steps = useMemo(() => stepsFor(context), [context]);
  const [draft, setDraft] = useState<ExceptionDraft>(initial);
  const [step, setStep] = useState<ExcStep>(steps[0]);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);
  const saveM = useSaveCampaignException();

  // Bug UAT #114 — campañas Brillamos ACTIVAS, para el selector de campaña base. Solo se piden en el
  // contexto Brillamos: Fundamentales no tiene campañas que elegir. Mismo hook y tope de 100 que usa
  // `AdminCampaigns` para detectar conflictos de alcance.
  const campanasQuery = useCampaigns('Activa', 1, 100, context === 'brillamos');
  const campanasBase = useMemo(
    () => (campanasQuery.data?.items ?? []).filter((c) => c.tipo === 'Brillamos'),
    [campanasQuery.data],
  );
  const campanaBase = useMemo(
    () => campanasBase.find((c) => c.id === draft.baseCampaignId) ?? null,
    [campanasBase, draft.baseCampaignId],
  );

  // Tarea 5 (R2a) — alcance delegado del admin: mismo patrón que `CampaignWizard` en
  // `AdminCampaigns.tsx` (ver `ouScopeUtils.ts`). `recognitionOuScope`, NO `ouScope` (Pulso): esta
  // pantalla es de Reconocimiento/Brillamos. Solo se aplica con el perfil YA resuelto — mientras
  // carga, filtrar dejaría el árbol vacío para TODOS (mismo hallazgo que en AdminCampaigns).
  const currentUser = useCurrentUser();
  const ouScope = currentUser?.profileResolved ? currentUser.recognitionOuScope : undefined;
  const scopeIds = useMemo(() => idsEnAlcance(ouScope, ouItems), [ouScope, ouItems]);
  // Si la excepción en EDICIÓN ya tiene OUs fuera del scope actual (creada/editada antes por un admin
  // con más alcance), se muestra el árbol COMPLETO: filtrarlas las escondería y provocaría
  // des-marcados fantasma al guardar, igual que en `AdminCampaigns.tsx`.
  const hasOutOfScope = campanaFueraDeAlcance(draft.alcance, scopeIds);
  const scopedItems = useMemo(
    () => (scopeIds && !hasOutOfScope ? ouItems.filter((item) => scopeIds.has(item.id)) : ouItems),
    [scopeIds, hasOutOfScope, ouItems],
  );
  // Red de seguridad (bug UAT #22, espejo de AdminCampaigns): admin acotado cuyo alcance no resuelve
  // a ninguna OU del catálogo actual. `ouItems.length > 0` evita confundir "catálogo aún cargando"
  // con "alcance vacío" (parpadeo).
  const scopeEmpty = scopeIds != null && ouItems.length > 0 && scopedItems.length === 0;

  // Preselecciona el alcance completo del admin no-global al CREAR una excepción nueva — default
  // cómodo, no bloqueo: el admin puede desmarcar libremente dentro de su scope.
  useEffect(() => {
    if (draft.id || !ouScope || ouScope.allOusAccess || ouScope.rootOuIds.length === 0) return;
    setDraft((d) => (d.alcance.length === 0 ? { ...d, alcance: [...ouScope.rootOuIds] } : d));
  }, [ouScope, draft.id]);

  const patch = (p: Partial<ExceptionDraft>) => { setDraft((d) => ({ ...d, ...p })); setDirty(true); };
  const stepIndex = steps.indexOf(step);
  const isLast = stepIndex === steps.length - 1;
  // El paso "Documentos" necesita `draft.id` (sube archivos contra la excepción ya creada) — mismo
  // criterio que `guardarVisibleEnPaso` en `AdminCampaigns.tsx`. Sin esto, el aviso "guarda el
  // borrador para gestionar sus documentos" del paso 'docs' no tenía ningún botón de guardado a la
  // vista hasta llegar al último paso del asistente (bug reportado por el usuario).
  const docsStepIndex = steps.indexOf('docs');
  const showSaveButton = Boolean(draft.id) || stepIndex >= docsStepIndex;
  // Con el toggle de horario ON, la ventana es obligatoria y coherente (fin > inicio). Los strings
  // datetime-local "YYYY-MM-DDTHH:mm" ordenan cronológicamente por comparación lexicográfica.
  const scheduleValid = !draft.scheduleEnabled
    || (!!draft.fechaInicio && !!draft.fechaFin && draft.fechaFin > draft.fechaInicio);
  /**
   * 🔴 Bug UAT #114 — la campaña base es OBLIGATORIA en Brillamos (decisión de Carlos, 20-ago-2026:
   * «hacerlo obligatorio es lo ideal; las huérfanas tendrán que ajustarse cuando se abran»).
   *
   * Corta de raíz la clase de defecto que generó tres filas del tracker: una excepción sin campaña
   * activa debajo se guardaba, se aprobaba, se mostraba «Activa»… y no aplicaba nada, sin que nada en
   * la pantalla lo dijera. Ahora no se puede ni guardar.
   *
   * Fundamentales queda fuera a propósito: es el programa default, siempre activo, sin campañas que
   * elegir.
   *
   * Consecuencia deliberada sobre lo que ya existe: las excepciones guardadas sin campaña declarada
   * —las 22 de QA lo están— abren con el selector vacío y exigen elegir una para volver a guardarse.
   * Es el ajuste que se pidió: se corrigen al abrirlas, no con una migración.
   */
  const baseCampaignValida = context !== 'brillamos' || Boolean(draft.baseCampaignId);
  const canSave = draft.alcance.length > 0 && scheduleValid && baseCampaignValida;

  /**
   * Por qué está apagado Guardar, para pintarlo JUNTO al botón. Se ordena por el paso en que se
   * corrige: primero el alcance (identidad de la excepción), luego la campaña base, luego la ventana.
   * Sin esto el botón quedaba mudo — ver la nota del `AdminSaveBar` más abajo.
   */
  const motivoBloqueo = draft.alcance.length === 0
    ? t('admin.campaigns.valScope')
    : !baseCampaignValida
      ? t('admin.campaigns.exc.baseCampaignRequerida')
      : !scheduleValid
        ? t('admin.campaigns.exc.scheduleInvalid')
        : null;

  // `after` solo se dispara al guardar desde el ÚLTIMO paso (ver nav abajo): un guardado temprano
  // desde 'docs' (para desbloquear la subida de documentos) debe quedarse en el asistente con el
  // `id` recién creado ya escrito en `draft`, no expulsar al admin a la tabla vía `onDone`.
  const handleSave = (after?: () => void) => {
    saveM.mutate({ id: draft.id, body: buildBody(draft, ouItems, context) }, {
      onSuccess: (saved) => {
        setDraft((d) => (d.id ? d : { ...d, id: saved.id }));
        setDirty(false);
        after?.();
      },
    });
  };
  // Guardar desde un paso intermedio (p. ej. 'docs', para desbloquear la subida de documentos) es un
  // guardado de borrador normal, sin aviso extra — igual que `AdminSaveBar` en `AdminCampaigns.tsx`
  // desde su paso 'documents'. Solo guardar desde el ÚLTIMO paso es "enviar a aprobación" de verdad
  // (la excepción completa queda pendiente del Admin Global) y pide confirmación explícita.
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  return (
    <div className="exc-panel">
      <div className="adm-toolbar">
        <button type="button" className="btn btn--link" onClick={onDone}><i className="fas fa-arrow-left" /> {t('admin.campaigns.exc.back')}</button>
        <span className="adm-spacer" />
        <span className="exc-wizard-title"><i className="fas fa-sliders-h" aria-hidden="true" /> {t(context === 'brillamos' ? 'admin.campaigns.exc.wizardTitleBrillamos' : 'admin.campaigns.exc.wizardTitle')}</span>
      </div>

      <div className="adm-wizard-layout">
        <div className="adm-wizard-steps">
          {steps.map((s, i) => (
            <button
              key={s}
              type="button"
              className={`adm-wizard-step ${step === s ? 'adm-wizard-step--on' : ''} ${i < stepIndex ? 'adm-wizard-step--done' : ''}`}
              onClick={() => setStep(s)}
            >
              <span className="adm-wizard-num">
                {i < stepIndex ? <i className="fas fa-check" /> : <i className={`fas ${EXC_STEP_ICONS[s]}`} />}
              </span>
              {t(EXC_STEP_LABELS[s])}
            </button>
          ))}
        </div>

        <div className="adm-wizard-main">
          <div className="adm-wizard-body">
            {step === 'schedule' && (
              <div className="exc-vis">
                {/*
                  * 🔴 Bug UAT #114 — «Campaña base». Es lo primero del asistente a propósito: una
                  * «campaña por OU» SOBREESCRIBE una campaña activa, y si no hay ninguna debajo no hace
                  * nada. Ese fue el retest fallido: se creó la excepción para CENTRAL, se aprobó, quedó
                  * «Activa» en su columna… y el colaborador siguió viendo «Reconocer», porque no existía
                  * campaña activa que cubriera esa OU (medido en QA: cero).
                  *
                  * El vínculo es INFORMATIVO — lo que decide si la excepción aplica sigue siendo alcance
                  * + programa. Sirve para dos cosas que faltaban: que se vea de qué campaña cuelga, y
                  * poder mostrar SU ventana de fechas, que es lo que se pedía al reportar «aparece sin
                  * horario de inicio / fin».
                  *
                  * La ventana del padre se muestra en SOLO LECTURA y NO se copia al override: copiarla
                  * convertiría la excepción en `HorarioRestringido` (ver `deriveTipo`) y desplazaría el
                  * override que el administrador sí quería. Es un dato de referencia, no un valor del
                  * formulario.
                  */}
                <div className="exc-card">
                  <div className="exc-card-title">
                    <i className="fas fa-diagram-project" aria-hidden="true" /> {t('admin.campaigns.exc.baseCampaignTitle')}
                  </div>
                  <p className="adm-note">{t('admin.campaigns.exc.baseCampaignHint')}</p>
                  <label className="adm-field">
                    <span>{t('admin.campaigns.exc.baseCampaignLabel')}</span>
                    <select
                      className="field"
                      value={draft.baseCampaignId ?? ''}
                      onChange={(e) => patch({ baseCampaignId: e.target.value || null })}
                    >
                      <option value="">{t('admin.campaigns.exc.baseCampaignNone')}</option>
                      {campanasBase.map((c) => (
                        <option key={c.id} value={c.id}>{getCampaignName(c, lang)}</option>
                      ))}
                    </select>
                  </label>

                  {/* Sin ninguna campaña Brillamos activa, la excepción no va a hacer nada: se dice aquí
                      y ahora, que es el único momento en que el administrador puede corregirlo. */}
                  {!campanasQuery.isLoading && campanasBase.length === 0 && (
                    <p className="adm-error" role="alert">
                      <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('admin.campaigns.exc.baseCampaignNinguna')}
                    </p>
                  )}

                  {/* Hay campañas para elegir pero no se eligió ninguna: el motivo por el que Guardar
                      está apagado, junto al campo que lo apaga. Convención del #111 — deshabilitar con
                      el motivo a la vista, nunca un botón mudo. */}
                  {campanasBase.length > 0 && !draft.baseCampaignId && (
                    <p className="adm-note adm-note--warn" role="alert">
                      <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('admin.campaigns.exc.baseCampaignRequerida')}
                    </p>
                  )}

                  {campanaBase && (
                    <p className="adm-note">
                      <i className="fas fa-calendar-check" aria-hidden="true" />{' '}
                      {campanaBase.fechaInicio && campanaBase.fechaFin
                        ? t('admin.campaigns.exc.baseCampaignVentana', {
                            inicio: campanaBase.fechaInicio.slice(0, 10),
                            fin: campanaBase.fechaFin.slice(0, 10),
                          })
                        : t('admin.campaigns.exc.baseCampaignSinVentana')}
                    </p>
                  )}
                </div>

                {/* Réplica del paso "Horarios" de la maqueta (solo Brillamos): ventana fecha/hora local +
                    zona horaria + toggle. Con el toggle ON la excepción se tipa HorarioRestringido y el
                    backend bloquea los reconocimientos de la campaña Brillamos fuera de la ventana. */}
                {/*
                  * 🔴 Bug UAT #114 (retest 19-ago) — «aparece sin horario de inicio/fin a pesar de que
                  * sí se estableció al crear la campaña». Los dos campos estaban SIEMPRE habilitados,
                  * pero `buildOverride` solo persiste la ventana `draft.scheduleEnabled ? … : null`:
                  * con el toggle de abajo apagado, lo tecleado se descartaba al guardar sin error ni
                  * aviso, y al reabrir la excepción los campos salían vacíos.
                  *
                  * Medido en QA antes del fix: de 22 excepciones, CERO tenían ventana guardada
                  * (`Override.FechaInicio`/`HoraInicio` nulos en todas, ninguna de tipo
                  * HorarioRestringido). No es que nadie la usara: es que nadie conseguía guardarla.
                  *
                  * Se deshabilitan los campos en vez de auto-encender el toggle a propósito. Derivar
                  * `scheduleEnabled` de las fechas cambiaría el `Tipo` de la excepción a
                  * `HorarioRestringido` (ver `deriveTipo`), y eso EMPIEZA A BLOQUEAR ENVÍOS fuera de
                  * la ventana: convertiría un fallo de captura en un cambio de comportamiento para
                  * quien solo quería anotar unas fechas. Mismo criterio que el bug UAT #111:
                  * deshabilitar con el motivo a la vista, nunca ocultar ni adivinar la intención.
                  */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-calendar-alt" aria-hidden="true" /> {t('admin.campaigns.exc.scheduleWindow')}</div>
                  <div className="adm-form-grid">
                    <label className="adm-field"><span>{t('admin.campaigns.exc.scheduleStart')}</span>
                      <input
                        type="datetime-local"
                        value={draft.fechaInicio}
                        onChange={(e) => patch({ fechaInicio: e.target.value })}
                      />
                    </label>
                    <label className="adm-field"><span>{t('admin.campaigns.exc.scheduleEnd')}</span>
                      <input
                        type="datetime-local"
                        value={draft.fechaFin}
                        onChange={(e) => patch({ fechaFin: e.target.value })}
                      />
                    </label>
                  </div>
                  {/* Qué implica capturar la ventana. Antes aquí decía «enciende el interruptor para
                      poder configurarla», con los campos deshabilitados: se descartó al probarlo en QA
                      (quitaba una capacidad existente y el interruptor se llama «Desactivar
                      reconocimientos», que no invita a encenderlo para poner fechas). */}
                  <p className="adm-note">{t('admin.campaigns.exc.scheduleWindowNota')}</p>
                </div>

                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-globe-americas" aria-hidden="true" /> {t('admin.campaigns.exc.scheduleTz')}</div>
                  <label className="adm-field">
                    <select className="field" value={draft.zonaHoraria} onChange={(e) => patch({ zonaHoraria: e.target.value })}>
                      {EXC_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </label>
                </div>

                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-ban" aria-hidden="true" /> {t('admin.campaigns.exc.scheduleRestrictTitle')} <ToggleHelp tooltipKey="scheduleEnabled" />
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.scheduleEnabled} onChange={(v) => patch({ scheduleEnabled: v })} /></label>
                  </div>
                  <p className="adm-note">{t('admin.campaigns.exc.scheduleRestrictNote')}</p>
                </div>
              </div>
            )}
            {step === 'banner' && (
              <div className="exc-vis">
                {/* Override del banner GLOBAL de "Brillamos Juntos" (solo Admin Global, GestionSettingsDto)
                    para el alcance de ESTA excepción — Tarea 14. Mismo formulario que `BrillamosBannerEditor`
                    (subtítulo + logo por idioma + preview), vía `BannerEditorFields` compartido. */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-image" aria-hidden="true" /> {t('admin.campaigns.exc.bannerTitle')}</div>
                  <p className="adm-note">{t('admin.campaigns.exc.bannerIntro')}</p>
                  <BannerEditorFields
                    value={{ subtitle: draft.bannerSubtitle, logoUrl: draft.bannerLogoUrl }}
                    onChange={(next) => patch({ bannerSubtitle: next.subtitle, bannerLogoUrl: next.logoUrl })}
                    scopeLabel={t('admin.campaigns.exc.bannerScopeLabel')}
                  />
                </div>
              </div>
            )}
            {step === 'scope' && (
              <>
                <p className="adm-note">{t('admin.campaigns.exc.scopeIntro')}</p>
                {scopeEmpty ? (
                  <p className="adm-note adm-note--warn" role="alert">
                    <i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.scopeEmpty')}
                  </p>
                ) : (
                  <div className="adm-field" role="group" aria-labelledby="exc-scope-label">
                    <span id="exc-scope-label">{t('admin.campaigns.fieldScope')}</span>
                    <OuTreeSelect items={scopedItems} mode="multi" variant="inline" value={draft.alcance} onChange={(alcance) => patch({ alcance })} />
                  </div>
                )}
              </>
            )}

            {step === 'docs' && (
              draft.id ? (
                <>
                  <p className="adm-note">{t('admin.campaigns.exc.docsIntro')}</p>
                  <ExceptionDocumentsStep exceptionId={draft.id} />
                </>
              ) : (
                <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.saveDraftFirst')}</p>
              )
            )}

            {step === 'visibility' && (
              <div className="exc-vis">
                {/* Desactivar envío (kill-switch) — réplica del toggle fa-ban de la maqueta. Es el override
                    principal: al encenderlo la excepción se tipa DesactivarEnvio y el backend BLOQUEA el envío
                    para el alcance (PR #24). Va primero por ser el efecto más fuerte. */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-ban" aria-hidden="true" /> {t('admin.campaigns.exc.disableTitle')} <ToggleHelp tooltipKey="disableSend" />
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.disableSend} onChange={(v) => patch({ disableSend: v })} /></label>
                  </div>
                  <p className="adm-note">{t('admin.campaigns.exc.disableNote')}</p>
                </div>

                {/* Entry Points — RN-PI03: override de qué puertas de envío están abiertas para el
                    alcance de esta excepción. Mismo control visual (switches tipo tarjeta,
                    single-select) que la Modalidad de Reconocimiento del wizard de campaña normal
                    (`AdminCampaigns.tsx`), para que ambos asistentes se vean y se comporten igual. */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-door-open" aria-hidden="true" /> {t('admin.campaigns.exc.entryPoints')} <ToggleHelp tooltipKey="entryPoints" />
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.entryOverride} onChange={(v) => patch({ entryOverride: v })} /></label>
                  </div>
                  <div className="adm-toggles-row adm-toggles-row--cards">
                    {(['app', 'postit', 'ambos'] as const).map((m) => (
                      <label key={m} className="adm-inline-toggle adm-inline-toggle--switch">
                        <input type="checkbox" checked={draft.entryPointMode === m} onChange={() => patch({ entryPointMode: m })} />
                        {t(ENTRY_POINT_MODE_LABEL_KEYS[m])}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Visibilidad del reconocimiento */}
                <div className="exc-card exc-card--accent">
                  <div className="exc-card-title"><i className="fas fa-eye" aria-hidden="true" /> {t('admin.campaigns.exc.visTitle')}
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.visOverride} onChange={(v) => patch({ visOverride: v })} /></label>
                  </div>
                  <p className="adm-note">{t('admin.campaigns.exc.visIntro')}</p>
                  <div className="adm-toggles-row">
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visText} onChange={(v) => patch({ visText: v })} /> {t('admin.campaigns.exc.visText')} <ToggleHelp tooltipKey="visText" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visFundamental} onChange={(v) => patch({ visFundamental: v })} /> {t('admin.campaigns.exc.visFundamental')} <ToggleHelp tooltipKey="visFundamental" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visSender} onChange={(v) => patch({ visSender: v })} /> {t('admin.campaigns.exc.visSender')} <ToggleHelp tooltipKey="visSender" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visHabit} onChange={(v) => patch({ visHabit: v })} /> {t('admin.campaigns.exc.visHabit')} <ToggleHelp tooltipKey="visHabit" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visRanking} onChange={(v) => patch({ visRanking: v })} /> {t('admin.campaigns.exc.visRanking')} <ToggleHelp tooltipKey="visRanking" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.visMultiFund} onChange={(v) => patch({ visMultiFund: v })} /> {t('admin.campaigns.exc.visMultiFund')} <ToggleHelp tooltipKey="visMultiFund" /></label>
                  </div>
                  <div className="adm-form-grid">
                    <label className="adm-field"><span>{t('admin.campaigns.exc.maxFundamentales')}</span>
                      <input
                        type="number" min={1} max={50} step={1} className="adm-num-input"
                        disabled={!draft.visMultiFund}
                        value={draft.maxFundamentales}
                        onChange={(e) => patch({ maxFundamentales: clampNumber(e.target.value, 1, 50, 2) })}
                      />
                    </label>
                  </div>
                </div>

                {/* Reglas de envío */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-paper-plane" aria-hidden="true" /> {t('admin.campaigns.exc.rulesTitle')}
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.reglasOverride} onChange={(v) => patch({ reglasOverride: v })} /></label>
                  </div>
                  <div className="adm-toggles-row">
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.ruleMultiPersona} onChange={(v) => patch({ ruleMultiPersona: v })} /> {t('admin.campaigns.exc.ruleMultiPersona')} <ToggleHelp tooltipKey="ruleMultiPersona" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.ruleComment} onChange={(v) => patch({ ruleComment: v })} /> {t('admin.campaigns.exc.ruleComment')} <ToggleHelp tooltipKey="commentRequired" /></label>
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.ruleCsv} onChange={(v) => patch({ ruleCsv: v })} /> {t('admin.campaigns.exc.ruleCsv')} <ToggleHelp tooltipKey="ruleBulkCsv" /></label>
                  </div>
                  <div className="adm-form-grid">
                    <label className="adm-field"><span>{t('admin.campaigns.exc.ruleMinChars')}</span>
                      <input
                        type="number" min={0} max={500} step={1} className="adm-num-input--md"
                        disabled={!draft.ruleComment}
                        value={draft.ruleMinChars}
                        onChange={(e) => patch({ ruleMinChars: clampNumber(e.target.value, 0, 500, 20) })}
                      />
                    </label>
                  </div>
                </div>

                {/* Moderación */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-gavel" aria-hidden="true" /> {t('admin.campaigns.exc.modTitle')}
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.modOverride} onChange={(v) => patch({ modOverride: v })} /></label>
                  </div>
                  <div className="adm-toggles-row">
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.modReport} onChange={(v) => patch({ modReport: v })} /> {t('admin.campaigns.exc.modReport')} <ToggleHelp tooltipKey="modReport" /></label>
                    {/* El backend solo tiene UN flag (`editarYEliminar`, "viajan juntos, como el
                        global"): un solo toggle en vez de Editar/Eliminar por separado, para no
                        capturar en la UI una combinación que el DTO no puede representar. */}
                    <label className="adm-toggle-row"><AdminToggle isOn={draft.modEditDelete} onChange={(v) => patch({ modEditDelete: v })} /> {t('admin.campaigns.exc.modEditDelete')} <ToggleHelp tooltipKey="modEditDelete" /></label>
                  </div>
                </div>

                {/* Racha */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-fire" aria-hidden="true" /> {t('admin.campaigns.exc.streakTitle')} <ToggleHelp tooltipKey="streakActive" />
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.streakEnabled} onChange={(v) => patch({ streakEnabled: v })} /></label>
                  </div>
                  <div className="adm-form-grid">
                    <label className="adm-field"><span>{t('admin.campaigns.exc.streakMin')}</span>
                      <input type="number" min={1} max={20} step={1} className="adm-num-input" disabled={!draft.streakEnabled} value={draft.streakMin} onChange={(e) => patch({ streakMin: clampNumber(e.target.value, 1, 20, 1) })} />
                    </label>
                    <label className="adm-field"><span>{t('admin.campaigns.exc.streakPeriod')}</span>
                      <select className="field" value={draft.streakPeriod} disabled={!draft.streakEnabled} onChange={(e) => patch({ streakPeriod: e.target.value === 'monthly' ? 'monthly' : 'weekly' })}>
                        <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                        <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Topes */}
                <div className="exc-card">
                  <div className="exc-card-title"><i className="fas fa-hand-paper" aria-hidden="true" /> {t('admin.campaigns.exc.limitsTitle')} <ToggleHelp tooltipKey="limitsActive" />
                    <label className="adm-toggle-row exc-card-toggle"><AdminToggle isOn={draft.limitsEnabled} onChange={(v) => patch({ limitsEnabled: v })} /></label>
                  </div>
                  <div className="adm-form-grid">
                    <label className="adm-field"><span>{t('admin.campaigns.exc.limitMax')}</span>
                      <input type="number" min={1} max={1000} step={1} className="adm-num-input--md" disabled={!draft.limitsEnabled} value={draft.limitMax} onChange={(e) => patch({ limitMax: clampNumber(e.target.value, 1, 1000, 3) })} />
                    </label>
                    <label className="adm-field"><span>{t('admin.campaigns.exc.limitFreq')}</span>
                      <select className="field" value={draft.limitFreq} disabled={!draft.limitsEnabled} onChange={(e) => patch({ limitFreq: e.target.value as FreqKey })}>
                        <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                        <option value="daily">{t('admin.campaigns.periodDaily')}</option>
                        <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Aviso de aprobación requerida */}
                <div className="exc-warn-box">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                  <div>
                    <div className="exc-warn-title">{t('admin.campaigns.exc.approvalRequiredTitle')}</div>
                    <p className="exc-warn-msg">{t('admin.campaigns.exc.approvalRequiredMsg')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {draft.alcance.length === 0 && <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.valScope')}</p>}
          {draft.scheduleEnabled && !scheduleValid && <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.exc.scheduleInvalid')}</p>}
          {saveM.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(saveM.error, t('admin.saveError'))}</p>}

          <div className="adm-wizard-nav">
            <button type="button" className="btn btn--secondary" onClick={onDone}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--secondary" disabled={stepIndex === 0} onClick={() => setStep(steps[Math.max(0, stepIndex - 1)])}>
              <i className="fas fa-chevron-left" /> {t('admin.campaigns.prev')}
            </button>
            {showSaveButton && (
              <AdminSaveBar
                onSave={() => (isLast ? setConfirmSubmit(true) : handleSave())}
                saving={saveM.isPending}
                /*
                 * 🔴 `dirty` a secas, y el motivo por `disabledReason`. Antes iba
                 * `dirty={dirty && canSave}`: metía la validación DENTRO de `dirty`, así que el botón
                 * quedaba apagado sin decir por qué — `AdminSaveBar` solo pinta el candado con el
                 * motivo si se lo pasan, y este asistente era el ÚNICO de los cuatro que no lo hacía
                 * (Gestión, Puntos y Recap sí). Reportado como «el botón de guardar no se habilita».
                 *
                 * Se notaba sobre todo con la campaña base obligatoria: al editar una excepción vieja
                 * —que no la trae— el botón moría, y la explicación vivía en la tarjeta del paso
                 * Horarios, que puede no ser el paso donde está el botón. Ahora el motivo viaja CON el
                 * botón, en el paso que sea. Convención del bug UAT #111.
                 */
                dirty={dirty}
                disabledReason={dirty && !canSave ? motivoBloqueo : null}
                saved={saveM.isSuccess}
                label={isLast ? t('admin.campaigns.submitApproval') : t('admin.campaigns.saveDraft')}
              />
            )}
            {!isLast && (
              <button type="button" className="btn btn--primary" onClick={() => setStep(steps[Math.min(steps.length - 1, stepIndex + 1)])}>
                {t('admin.campaigns.next')} <i className="fas fa-chevron-right" />
              </button>
            )}
          </div>
        </div>
      </div>
      {confirmSubmit && (
        <ConfirmModal
          type="info"
          title={t('admin.campaigns.submitApprovalConfirmTitle')}
          message={t('admin.campaigns.submitApprovalConfirmMsg')}
          confirmText={t('admin.campaigns.submitApproval')}
          onConfirm={() => handleSave(onDone)}
          onClose={() => setConfirmSubmit(false)}
        />
      )}
    </div>
  );
}

export default CampaignExceptionsPanel;
